// Self-service BKLS beat licence checkout.
//
// Follows the same shape as StripeEmbeddedShopCheckout: explicit consent
// gate first, Stripe Embedded Checkout second. Differences that matter:
//
//  * `createBeatCheckout` is auth-protected (requireSupabaseAuth), so there
//    is no guest path — signed-out visitors are routed to /auth with the
//    beat and tier preserved in the redirect so they land back here.
//  * Only fixed-price tiers reach this component. Quote-only tiers stay on
//    the custom-request flow and are refused server-side anyway.
import { useEffect, useRef, useState } from "react";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { Link } from "@tanstack/react-router";
import { getStripe } from "@/lib/stripe";
import { createBeatCheckout } from "@/lib/bkls.functions";

export interface BeatLicenseCheckoutProps {
  slug: string;
  tierKey: string;
  tierName: string;
  beatTitle: string;
  priceLabel: string;
  isAuthenticated: boolean;
  /** Fired once, when the customer commits to the payment step. */
  onCheckoutStart?: () => void;
  onCancel: () => void;
  returnUrl?: string;
}

/**
 * Sign-in destination that brings the visitor straight back to this beat with
 * the chosen tier reselected.
 */
export function beatCheckoutRedirect(slug: string, tierKey: string): string {
  return `/beats/${slug}?tier=${encodeURIComponent(tierKey)}`;
}

export function BeatLicenseCheckout({
  slug,
  tierKey,
  tierName,
  beatTitle,
  priceLabel,
  isAuthenticated,
  onCheckoutStart,
  onCancel,
  returnUrl,
}: BeatLicenseCheckoutProps) {
  const [consent, setConsent] = useState({ terms: false, license: false });
  const [proceed, setProceed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Move focus into the panel when it opens so keyboard and screen-reader
  // users are not stranded at the CTA they just activated.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  // Escape closes the panel, matching dialog expectations.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onCancel();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onCancel]);

  const canProceed = consent.terms && consent.license;

  const fetchClientSecret = async (): Promise<string> => {
    setLoading(true);
    try {
      const finalReturnUrl =
        returnUrl ?? `${window.location.origin}/checkout/return?session_id={CHECKOUT_SESSION_ID}`;
      const result = await createBeatCheckout({
        data: {
          slug,
          tierKey,
          returnUrl: finalReturnUrl,
          consent: { terms: true, license: true },
        },
      });
      if ("error" in result) throw new Error(result.error);
      if (!result.clientSecret) throw new Error("Payment could not be started. Please try again.");
      setError(null);
      return result.clientSecret;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Payment could not be started. Please try again.";
      setError(message);
      setProceed(false);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const panelClass = "rounded-2xl border border-gold/40 bg-obsidian/60 p-5 sm:p-6";

  return (
    <div className={panelClass} role="group" aria-labelledby={`beat-checkout-heading-${tierKey}`}>
      <h3
        id={`beat-checkout-heading-${tierKey}`}
        ref={headingRef}
        tabIndex={-1}
        className="font-serif text-lg text-gold-luminous outline-none"
      >
        {tierName} licence · {beatTitle}
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">{priceLabel}</p>

      <p aria-live="polite" role="status" className="sr-only">
        {error
          ? `Checkout error: ${error}`
          : loading
            ? "Starting secure payment."
            : proceed
              ? "Secure payment form loaded."
              : "Licence checkout opened. Confirm the required agreements to continue."}
      </p>

      {!isAuthenticated ? (
        <div className="mt-5 space-y-3">
          <p className="text-sm text-muted-foreground">
            Licences are issued to an account so your files, agreement and receipt stay attached to you. Sign in
            to continue — we&apos;ll bring you straight back to this tier.
          </p>
          <Link
            to="/auth"
            search={{ redirect: beatCheckoutRedirect(slug, tierKey) }}
            className="inline-flex min-h-11 w-full items-center justify-center rounded-full bg-gold-gradient px-6 py-3 text-sm font-semibold text-obsidian shadow-gold sm:w-auto"
          >
            Sign in to license this beat
          </Link>
        </div>
      ) : proceed ? (
        <div id={`beat-checkout-${tierKey}`} className="mt-5 rounded-lg border hairline bg-obsidian/40 p-2">
          <EmbeddedCheckoutProvider stripe={getStripe()} options={{ fetchClientSecret }}>
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </div>
      ) : (
        <>
          <fieldset className="mt-5 space-y-2">
            <legend className="mb-2 text-xs uppercase tracking-[0.25em] text-gold">Required agreements</legend>
            <ConsentCheckbox
              checked={consent.terms}
              onChange={(v) => setConsent((c) => ({ ...c, terms: v }))}
              label={
                <>
                  I agree to the{" "}
                  <Link to="/terms" className="text-gold underline">
                    Terms of Service
                  </Link>
                  .
                </>
              }
            />
            <ConsentCheckbox
              checked={consent.license}
              onChange={(v) => setConsent((c) => ({ ...c, license: v }))}
              label={
                <>
                  I have read and accept the{" "}
                  <Link to="/beats/license" className="text-gold underline">
                    beat licence terms
                  </Link>{" "}
                  for this tier. This grants a licence only — copyright is never transferred.
                </>
              }
            />
          </fieldset>

          <button
            type="button"
            disabled={!canProceed || loading}
            onClick={() => {
              onCheckoutStart?.();
              setError(null);
              setProceed(true);
            }}
            className="mt-5 min-h-12 w-full rounded-full bg-gold-gradient px-6 py-3 text-sm font-semibold text-obsidian shadow-gold disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? "Starting secure payment…" : "Continue to secure payment"}
          </button>
        </>
      )}

      {error && (
        <p className="mt-4 rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200">{error}</p>
      )}

      <button
        type="button"
        onClick={onCancel}
        className="mt-3 min-h-11 w-full rounded-full border hairline px-4 py-2 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:border-gold hover:text-gold"
      >
        ← Back to licence options
      </button>
    </div>
  );
}

function ConsentCheckbox({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: React.ReactNode;
}) {
  return (
    <label className="flex items-start gap-3 text-sm">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-5 w-5 accent-gold"
      />
      <span className="text-muted-foreground">{label}</span>
    </label>
  );
}
